import { useContext, useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import { SpinnerWrapper } from 'components/SpinnerWrapper';
import { ProgramSetupView } from './ProgramSetupView';
import { useProgramSetupForm } from './ProgramSetupForm';
import { useProgramSetupService } from 'shared/services/programSetupService';
import { useChannelService } from 'shared/services/channelService';
import { useDebouncedState } from 'shared/hooks/useDebouncedState';
import { useSelectOptions } from 'shared/hooks/useSelectOptions';
import { DefaultOptionType } from 'antd/lib/select';
import { IChannel } from 'shared/interfaces/IChannel';
import { useProgramService } from 'shared/services/programService';
import { IProgram } from 'shared/interfaces/IProgram';
import { useAuthorService } from 'shared/services/authorService';
import { AUTHORSORT } from 'shared/constants/AUTHOR_SORT';
import { IAuthor } from 'shared/interfaces/IAuthor';
// import { ProgramSetupAnchor } from 'shared/interfaces/IProgramSetup';
import { BreadcrumbContext } from 'shared/context/Breadcrumb';
import { useCountryService } from 'shared/services/countryService';
import { ICountry } from 'shared/interfaces/ICountry';
import { ChapterSchedule, IProgramSetupForm } from 'shared/interfaces/IProgramSetup';
import { Alert } from 'shared/theme/elements';
import { ROUTES } from 'shared/constants/ROUTES';
import { useQueryClient } from 'react-query';
import { DrawerBody } from './ProgramSetup.styled';
import { DrawerHead } from './components/DrawerHead';
import { getChannelRecording } from 'components/ProgramSetupChannelSettings';
import { useSelectedProgramType } from 'shared/hooks/useSelectedProgramType';

interface Props {
  editId?: string;
  defaultChannelId?: string;
  defaultChannelName?: string;
  onClose: () => void;
}

export interface ProgramSetupSearch {
  tvChannel?: string;
  tvProgram?: string;
  radioStation?: string;
  radioStationProgram?: string;
  authors?: string;
  country?: string;
}

export interface ProgramSetupPageSearchOptions {
  tvChannel?: DefaultOptionType[];
  tvProgram?: DefaultOptionType[];
  radioStation?: DefaultOptionType[];
  radioStationProgram?: DefaultOptionType[];
  authors?: DefaultOptionType[];
  country?: DefaultOptionType[];
}

export interface ProgramSetupPageSearchLoading {
  tvChannel?: boolean;
  tvProgram?: boolean;
  radioStation?: boolean;
  radioStationProgram?: boolean;
  authors?: boolean;
  country?: boolean;
}

const ProgramSetupContainer = ({ editId, defaultChannelId, defaultChannelName, onClose }: Props) => {
  const user = localStorage?.getItem('user_admin') ? JSON.parse(localStorage.getItem('user_admin') || '') : null;

  const queryClient = useQueryClient();
  const { push } = useHistory();
  const { setAdditionalValue } = useContext(BreadcrumbContext);

  // Program Setup Data
  const { getProgramSetup, createProgramSetup, updateProgramSetup } = useProgramSetupService();
  const { createMutation } = createProgramSetup();
  const { updateMutation } = updateProgramSetup();
  const {
    data: programSetupData,
    isLoading: programSetupLoading,
    isFetching: programSetupFetching,
  } = getProgramSetup(
    editId
      ? {
          _id: editId,
          audio_setup: null,
        }
      : undefined,
  );
  const programSetup = useMemo(() => programSetupData?.data?.[0], [programSetupData?.data]);

  // Form
  const { form } = useProgramSetupForm({ programSetup });
  const { getValues, setValue, reset, handleSubmit, formState } = form;

  // A new program inherits the channel-level recording config from any program already on that channel.
  const selectedChannel = useWatch({ control: form.control, name: 'channel_obj' });
  const { data: channelProgramData } = getProgramSetup(
    !editId && selectedChannel
      ? { page: 1, limit: 1, sort: '', channel_obj_id: selectedChannel, audio_setup: null }
      : undefined,
  );
  const channelProgram = channelProgramData?.data?.[0];
  const inheritedRecording = useMemo(
    () => (programSetup ? getChannelRecording(programSetup) : channelProgram && getChannelRecording(channelProgram)),
    [programSetup, channelProgram],
  );

  // The selected program carries the Program Type shown (and edited) in this form.
  const selectedProgramId = useWatch({ control: form.control, name: 'program_obj' });
  const {
    program: selectedProgram,
    saveProgramType,
    saving: savingProgramType,
  } = useSelectedProgramType(selectedProgramId);

  // TV Channel Data
  const [tvChannelSearch, setTVChannelSearch] = useDebouncedState<string | undefined>(undefined, 800);
  const { getChannel } = useChannelService();
  const { data: tvChannel, isLoading: tvChannelDataLoading } = getChannel({
    media_source_type_flag: ['video'],
    channel_name: tvChannelSearch,
    limit: 20,
    country: ['PHILIPPINES'],
  });
  const tvChannelData = useMemo(() => tvChannel?.data, [tvChannel?.data]);
  // Options
  const { data: tvChannelOptions } = useSelectOptions<IChannel>({
    data: tvChannelData,
    valueKey: 'publisher_obj_id',
    labelKey: 'publisher_name',
    initialValues: () => {
      if (programSetup?.channel_details) {
        return [
          {
            value: programSetup?.channel_details._id,
            label: programSetup?.channel_details.channel_name,
          },
        ];
      }

      if (defaultChannelId && defaultChannelName) {
        return [{ value: defaultChannelId, label: defaultChannelName }];
      }

      return [];
    },
  });

  // TV Program Data
  const [tvProgramSearch, setTVProgramSearch] = useDebouncedState<string | undefined>(undefined, 800);
  const { getProgram } = useProgramService();
  const { data: tvProgram, isLoading: tvProgramDataLoading } = getProgram({
    channel_id: getValues().channel_obj,
    program_name: tvProgramSearch,
  });
  const tvProgramData = useMemo(() => tvProgram?.data, [tvProgram?.data]);
  // Options
  const { data: tvProgramOptions } = useSelectOptions<IProgram>({
    data: tvProgramData,
    valueKey: '_id',
    labelKey: 'program_name',
    initialValues: () => {
      if (!programSetup?.program_details) return [];

      return [
        {
          value: programSetup?.program_details._id,
          label: programSetup?.program_details.program_name,
        },
      ];
    },
  });

  // Radio Station Data
  const [radioStationSearch, setRadioStationSearch] = useDebouncedState<string | undefined>(undefined, 800);
  const { data: radioStation, isLoading: radioStationDataLoading } = getChannel({
    media_source_type_flag: ['audio'],
    channel_name: radioStationSearch,
    limit: 20,
    country: ['PHILIPPINES'],
  });
  const radioStationData = useMemo(() => radioStation?.data, [radioStation?.data]);
  // Options
  const { data: radioStationOptions } = useSelectOptions<IChannel>({
    data: radioStationData,
    valueKey: 'publisher_obj_id',
    labelKey: 'publisher_name',
    initialValues: () => {
      if (!programSetup?.station_details) return [];

      return [
        {
          value: programSetup?.station_details._id,
          label: programSetup?.station_details.station_name,
        },
      ];
    },
  });

  // Video Program Data
  const [radioStationProgramSearch, setRadioStationProgramSearch] = useDebouncedState<string | undefined>(
    undefined,
    800,
  );
  const { data: radioStationProgram, isLoading: radioStationProgramDataLoading } = getProgram({
    channel_id: getValues().station_obj ?? undefined,
    program_name: radioStationProgramSearch,
  });
  const radioStationProgramData = useMemo(() => radioStationProgram?.data, [radioStationProgram?.data]);
  // Options
  const { data: radioStationProgramOptions } = useSelectOptions<IProgram>({
    data: radioStationProgramData,
    valueKey: '_id',
    labelKey: 'program_name',
    initialValues: () => {
      if (!programSetup?.station_program_details) return [];

      return [
        {
          value: programSetup?.station_program_details._id,
          label: programSetup?.station_program_details.station_program_name,
        },
      ];
    },
  });

  // Authors Data
  const [authorSearch, setAuthorSearch] = useDebouncedState<string | undefined>(undefined, 800);
  const { getAuthors } = useAuthorService();
  const { data: author, isLoading: authorDataLoading } = getAuthors({
    author_name: authorSearch,
    is_parent: true,
    is_verified: true,
    limit: 20,
    sort: authorSearch ? undefined : AUTHORSORT.NAMEASC,
    author_tag: 'Author',
  });
  const authorData = useMemo(() => author?.data, [author?.data]);
  // Options
  const { data: authorOptions } = useSelectOptions<IAuthor>({
    data: authorData,
    valueKey: 'author_obj_id',
    labelKey: 'author_name',
    initialValues: () => {
      if (!programSetup?.anchors) return [];

      return programSetup?.anchors.map(({ _id, author_name }) => ({
        value: _id,
        label: author_name,
      }));
    },
  });

  // Country Data
  const [countrySearch, setCountrySearch] = useDebouncedState<string | undefined>(undefined, 800);
  const { getCountries } = useCountryService();
  const { data: country, isLoading: countryLoading } = getCountries({
    country_name: countrySearch,
  });
  const countryData = useMemo(() => country?.data, [country?.data]);
  // Options
  const { data: countryOptions } = useSelectOptions<ICountry>({
    data: countryData,
    valueKey: 'country_name',
    labelKey: 'country_name',
    initialValues: () => {
      if (!programSetup?.country) return [];

      return [
        {
          value: programSetup?.country,
          label: programSetup?.country,
        },
      ];
    },
  });

  // Search Options
  const options: ProgramSetupPageSearchOptions = useMemo(
    () => ({
      tvChannel: tvChannelOptions,
      tvProgram: tvProgramOptions,
      radioStation: radioStationOptions,
      radioStationProgram: radioStationProgramOptions,
      authors: authorOptions,
      country: countryOptions,
    }),
    [
      authorOptions,
      countryOptions,
      radioStationOptions,
      radioStationProgramOptions,
      tvChannelOptions,
      tvProgramOptions,
    ],
  );

  // Search Loading
  const searchLoading: ProgramSetupPageSearchLoading = useMemo(
    () => ({
      tvChannel: tvChannelDataLoading,
      tvProgram: tvProgramDataLoading,
      radioStation: radioStationDataLoading,
      raddioStationProgram: radioStationProgramDataLoading,
      authors: authorDataLoading,
      country: countryLoading,
    }),
    [
      tvChannelDataLoading,
      tvProgramDataLoading,
      radioStationDataLoading,
      radioStationProgramDataLoading,
      authorDataLoading,
      countryLoading,
    ],
  );

  const handleOnSearch = (key: keyof ProgramSetupSearch, value: any) => {
    switch (key) {
      case 'tvChannel':
        setTVChannelSearch(value);
        break;
      case 'tvProgram':
        setTVProgramSearch(value);
        break;
      case 'radioStation':
        return setRadioStationSearch(value);
      case 'radioStationProgram':
        return setRadioStationProgramSearch(value);
      case 'authors':
        setAuthorSearch(value);
        break;
      case 'country':
        setCountrySearch(value);
        break;
    }
  };

  const submitProgram = (fields: IProgramSetupForm) => {
    if (programSetup?._id) {
      updateMutation.mutateAsync(
        {
          data: { _id: programSetup._id, updated_by: user?._id, ...fields },
          image: {},
        },
        {
          onSuccess: () => {
            Alert.success('Program Setup Updated!');
            queryClient.invalidateQueries('program_setup');
            push(ROUTES.TV.PROGRAMSETUP.LIST);
          },
          onError: (error: any) => {
            Alert.error(error.response?.data.server_response || 'Something went wrong');
          },
        },
      );
    } else {
      createMutation.mutate(
        {
          data: { ...fields, created_by: user?._id },
          image: {},
        },
        {
          onSuccess: () => {
            Alert.success('Program Setup Created!');
            queryClient.invalidateQueries('program_setup');
            push(ROUTES.TV.PROGRAMSETUP.LIST);
          },
          onError: (error: any) => {
            Alert.error(error.response.data.server_response);
          },
        },
      );
    }
  };

  const sanitizeChapterSchedule = (chapterSchedule: ChapterSchedule): ChapterSchedule => {
    const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const sanitized: Partial<ChapterSchedule> = {};
    allDays.forEach((day) => {
      sanitized[day] = {
        chapter_start: chapterSchedule?.[day]?.chapter_start ?? '',
        chapter_end: chapterSchedule?.[day]?.chapter_end ?? '',
      };
    });
    return sanitized as ChapterSchedule;
  };

  const triggerSubmit = handleSubmit(async ({ is_simulcast, program_type, ...values }: IProgramSetupForm) => {
    // Program Type is saved on the program itself, before the setup, so a rejected type changes nothing.
    try {
      await saveProgramType(program_type, user?._id);
    } catch (error: any) {
      Alert.error(error?.response?.data?.server_response || error?.message || 'Failed to update Program Type');
      return;
    }
    let sanitizedValues: IProgramSetupForm = values;
    if (values.chapter_schedule) {
      sanitizedValues = { ...sanitizedValues, chapter_schedule: sanitizeChapterSchedule(values.chapter_schedule) };
    }
    if (!is_simulcast) {
      sanitizedValues = { ...sanitizedValues, station_obj: null, station_program_obj: null };
    }
    // Recording config is channel-level: a new program takes whatever its channel already uses,
    // except Status and Sagap Process, which are its own.
    if (!programSetup && channelProgram) {
      sanitizedValues = {
        ...sanitizedValues,
        local_recording_details: { ...channelProgram.local_recording_details },
        recording_title_setup: channelProgram.recording_title_setup
          ? {
              ...channelProgram.recording_title_setup,
              on_off: values.recording_title_setup?.on_off,
              sagap_process: values.recording_title_setup?.sagap_process,
            }
          : sanitizedValues.recording_title_setup,
      };
    }
    submitProgram(sanitizedValues);
  });

  useEffect(() => {
    reset();
    if (programSetup) {
      setAdditionalValue(
        `${programSetup?.channel_details.channel_name} - ${programSetup?.program_details.program_name}`,
      );
    }

    return () => {
      setAdditionalValue(undefined);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programSetup]);

  useEffect(() => {
    queryClient.invalidateQueries('program_setup');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Default the Channel on Create to whatever Channel the list was filtered to.
  useEffect(() => {
    if (!editId && defaultChannelId) {
      setValue('channel_obj', defaultChannelId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Runs after the form resets above, so the loaded program's type isn't wiped by the reset.
  useEffect(() => {
    setValue('program_type', selectedProgram?.program_type ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programSetup, selectedProgram?._id, selectedProgram?.program_type]);

  const isSaving = updateMutation.isLoading || createMutation.isLoading || savingProgramType;
  const title = programSetup
    ? `${programSetup.channel_details.channel_name} — ${programSetup.program_details.program_name}`
    : 'Create New Program Setup';

  return (
    <>
      <DrawerHead
        eyebrow="TV · Program Setup"
        title={title}
        dirty={formState.isDirty}
        saveLabel={programSetup ? 'Update Program' : 'Create Program'}
        saving={isSaving}
        onCancel={onClose}
        onSave={triggerSubmit}
        onClose={onClose}
      />
      <DrawerBody>
        <SpinnerWrapper spinning={programSetupLoading || programSetupFetching || isSaving} position="top">
          <ProgramSetupView
            form={form}
            programSetup={programSetup}
            options={options}
            searchLoading={searchLoading}
            onSearch={handleOnSearch}
            isLoading={programSetupLoading}
            inheritedRecording={inheritedRecording}
            program={selectedProgram}
          />
        </SpinnerWrapper>
      </DrawerBody>
    </>
  );
};

export default ProgramSetupContainer;
